import { ReactNode } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AppsIcon from '@mui/icons-material/Apps';
import FolderIcon from '@mui/icons-material/Folder';
import SecurityIcon from '@mui/icons-material/Security';
import ExtensionIcon from '@mui/icons-material/Extension';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GppGoodIcon from '@mui/icons-material/GppGood';

// Define HTTP methods type
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

// API template categories
export const apiTemplateCategories = [
  { id: 'users', name: 'Users', icon: PersonIcon },
  { id: 'groups', name: 'Groups', icon: GroupIcon },
  { id: 'applications', name: 'Applications', icon: AppsIcon },
  { id: 'directory', name: 'Directory', icon: FolderIcon },
  { id: 'policies', name: 'Policies', icon: SecurityIcon },
  { id: 'conditionalAccess', name: 'Conditional Access', icon: GppGoodIcon },
  { id: 'advanced', name: 'Advanced', icon: ExtensionIcon },
];

// Template interface
export interface ApiTemplate {
  id: string;
  category: string;
  name: string;
  method: HttpMethod;
  endpoint: string;
  body: string;
  headers: string;
  description: string;
}

// API templates
export const apiTemplates: ApiTemplate[] = [
  // Users category
  {
    id: 'user-get-me',
    category: 'users',
    name: 'Get My Profile',
    method: 'GET',
    endpoint: '/me',
    body: '',
    headers: '{}',
    description: 'Gets information about the current user'
  },
  {
    id: 'user-list-all',
    category: 'users',
    name: 'List All Users',
    method: 'GET',
    endpoint: '/users?$top=10',
    body: '',
    headers: '{}',
    description: 'List users in the organization'
  },
  {
    id: 'user-get-photo',
    category: 'users',
    name: 'Get User Photo',
    method: 'GET',
    endpoint: '/me/photo/$value',
    body: '',
    headers: '{}',
    description: 'Gets the current user\'s profile photo'
  },
  {
    id: 'user-get-manager',
    category: 'users',
    name: 'Get My Manager',
    method: 'GET',
    endpoint: '/me/manager',
    body: '',
    headers: '{}',
    description: 'Gets the current user\'s manager'
  },
  {
    id: 'user-create',
    category: 'users',
    name: 'Create User',
    method: 'POST',
    endpoint: '/users',
    body: JSON.stringify({
      "accountEnabled": true,
      "displayName": "Test User",
      "mailNickname": "testuser",
      "userPrincipalName": "testuser@contoso.onmicrosoft.com",
      "passwordProfile": {
        "forceChangePasswordNextSignIn": true,
        "password": "xWwvJ]6NMw+bWH-d"
      }
    }, null, 2),
    headers: '{}',
    description: 'Creates a new user account'
  },
  
  // Groups category
  {
    id: 'groups-list-all',
    category: 'groups',
    name: 'List All Groups',
    method: 'GET',
    endpoint: '/groups?$top=10',
    body: '',
    headers: '{}',
    description: 'Lists groups in the organization'
  },
  {
    id: 'groups-get-members',
    category: 'groups',
    name: 'Get Group Members',
    method: 'GET',
    endpoint: '/groups/{group-id}/members',
    body: '',
    headers: '{}',
    description: 'Lists members of a group'
  },
  
  // Applications category
  {
    id: 'apps-list',
    category: 'applications',
    name: 'List Applications',
    method: 'GET',
    endpoint: '/applications?$top=10',
    body: '',
    headers: '{}',
    description: 'Lists app registrations in the tenant'
  },
  {
    id: 'apps-get-by-id',
    category: 'applications',
    name: 'Get App by ID',
    method: 'GET',
    endpoint: '/applications/{app-id}',
    body: '',
    headers: '{}',
    description: 'Gets details of a specific application'
  },
  {
    id: 'apps-service-principals',
    category: 'applications',
    name: 'List Service Principals',
    method: 'GET',
    endpoint: '/servicePrincipals?$top=10',
    body: '',
    headers: '{}',
    description: 'Lists service principals in the tenant'
  },
  {
    id: 'app-add-password',
    category: 'applications',
    name: 'Add Client Secret',
    method: 'POST',
    endpoint: '/applications/{app-id}/addPassword',
    body: JSON.stringify({
      "passwordCredential": {
        "displayName": "API Access Secret",
        "endDateTime": "2024-09-30T00:00:00Z"
      }
    }, null, 2),
    headers: '{}',
    description: 'Creates a new client secret for an app'
  },
  
  // Directory operations
  {
    id: 'directory-roles',
    category: 'directory',
    name: 'List Directory Roles',
    method: 'GET',
    endpoint: '/directoryRoles',
    body: '',
    headers: '{}',
    description: 'Lists all directory roles in the tenant'
  },
  {
    id: 'directory-role-members',
    category: 'directory',
    name: 'Get Role Members',
    method: 'GET',
    endpoint: '/directoryRoles/{role-id}/members',
    body: '',
    headers: '{}',
    description: 'Lists members of a directory role'
  },
  
  // Policies
  {
    id: 'policies-token-lifetime',
    category: 'policies',
    name: 'Token Lifetime Policies',
    method: 'GET',
    endpoint: '/policies/tokenLifetimePolicies',
    body: '',
    headers: '{}',
    description: 'Lists token lifetime policies'
  },
  {
    id: 'policies-claims-mapping',
    category: 'policies',
    name: 'Claims Mapping Policies',
    method: 'GET',
    endpoint: '/policies/claimsMappingPolicies',
    body: '',
    headers: '{}',
    description: 'Lists claims mapping policies'
  },
    // Conditional Access Policies
  {
    id: 'ca-list-policies',
    category: 'conditionalAccess',
    name: 'List CA Policies',    method: 'GET',
    endpoint: '/identity/conditionalAccess/policies',
    body: '',
    headers: '{}',
    description: 'Lists all Conditional Access policies in the tenant'
  },
  {
    id: 'ca-get-policy',
    category: 'conditionalAccess',
    name: 'Get CA Policy by ID',
    method: 'GET',
    endpoint: '/identity/conditionalAccess/policies/{policy-id}',
    body: '',
    headers: '{}',
    description: 'Get details of a specific Conditional Access policy'
  },
  {
    id: 'ca-update-policy',
    category: 'conditionalAccess',
    name: 'Update CA Policy',
    method: 'PATCH',
    endpoint: '/identity/conditionalAccess/policies/{policy-id}',
    body: JSON.stringify({
      "displayName": "Updated Policy Name",
      "state": "enabled"
    }, null, 2),
    headers: '{}',
    description: 'Update properties of an existing Conditional Access policy'
  },
  {
    id: 'ca-create-policy',
    category: 'conditionalAccess',
    name: 'Create CA Policy',
    method: 'POST',
    endpoint: '/identity/conditionalAccess/policies',
    body: JSON.stringify({
      "displayName": "Block access from non-trusted locations",
      "state": "disabled", // Start disabled for safety
      "conditions": {
        "clientAppTypes": ["all"],
        "applications": {
          "includeApplications": ["All"]
        },
        "users": {
          "includeGroups": ["00000000-0000-0000-0000-000000000000"], // Replace with actual group ID
          "excludeGroups": [],
          "includeRoles": [],
          "excludeRoles": []
        },
        "locations": {
          "includeLocations": ["All"],
          "excludeLocations": ["AllTrusted"]
        }
      },
      "grantControls": {
        "operator": "OR",
        "builtInControls": ["block"]
      }
    }, null, 2),
    headers: '{}',
    description: 'Creates a new Conditional Access policy (disabled by default)'
  },
  {
    id: 'ca-named-locations',
    category: 'conditionalAccess',
    name: 'List Named Locations',
    method: 'GET',
    endpoint: '/identity/conditionalAccess/namedLocations',
    body: '',
    headers: '{}',
    description: 'Lists all named locations configured for Conditional Access'
  },  {
    id: 'ca-authentication-strength',
    category: 'conditionalAccess',
    name: 'Authentication Strengths',
    method: 'GET',
    endpoint: '/identity/conditionalAccess/authenticationStrengths/policies',
    body: '',
    headers: '{}',
    description: 'Lists authentication strength policies used in Conditional Access'
  },
  {
    id: 'ca-temp-access-pass',
    category: 'conditionalAccess',
    name: 'Create Temporary Access Pass',
    method: 'POST',
    endpoint: '/users/{user-id}/authentication/temporaryAccessPassMethods',
    body: JSON.stringify({
      "lifetimeInMinutes": 60,
      "isUsableOnce": true
    }, null, 2),
    headers: '{}',
    description: 'Create a temporary access pass for passwordless authentication'
  },
  {
    id: 'id-risky-users',
    category: 'conditionalAccess',
    name: 'List Risky Users',
    method: 'GET',
    endpoint: '/identityProtection/riskyUsers',
    body: '',
    headers: '{}',
    description: 'Lists users flagged for risk by Azure AD Identity Protection'
  },
  {
    id: 'id-risk-detections',
    category: 'conditionalAccess',
    name: 'Risk Detections',
    method: 'GET',
    endpoint: '/identityProtection/riskDetections',
    body: '',
    headers: '{}',
    description: 'Lists risk detections from Azure AD Identity Protection'
  },
    // Authentication Methods
  {
    id: 'auth-methods-user',
    category: 'advanced',
    name: 'User Auth Methods',
    method: 'GET',
    endpoint: '/users/{user-id}/authentication/methods',
    body: '',
    headers: '{}',
    description: 'Lists all authentication methods registered for a user'
  },
  {
    id: 'auth-methods-policy',
    category: 'advanced',
    name: 'Auth Methods Policy',
    method: 'GET',
    endpoint: '/policies/authenticationMethodsPolicy',
    body: '',
    headers: '{}',
    description: 'Gets the authentication methods policy configuration'
  },
  {
    id: 'token-issuance-policy',
    category: 'advanced',
    name: 'Token Issuance Policies',
    method: 'GET',
    endpoint: '/policies/tokenIssuancePolicies',
    body: '',
    headers: '{}',
    description: 'Lists token issuance policies in the tenant'
  },
  
  // Advanced queries
  {
    id: 'advanced-app-roles',
    category: 'advanced',
    name: 'Apps with App Roles',
    method: 'GET',
    endpoint: '/applications?$filter=appRoles/any()',
    body: '',
    headers: '{"ConsistencyLevel": "eventual"}',
    description: 'Finds apps that define app roles'
  },
  {
    id: 'advanced-batch',
    category: 'advanced',
    name: 'Batch Request',
    method: 'POST',
    endpoint: '/$batch',
    body: JSON.stringify({
      "requests": [
        {
          "id": "1",
          "method": "GET",
          "url": "/users?$top=3"
        },
        {
          "id": "2",
          "method": "GET",
          "url": "/groups?$top=3"
        },
        {
          "id": "3",
          "method": "GET", 
          "url": "/me"
        }
      ]    }, null, 2),
    headers: '{}',
    description: 'Executes multiple requests in a single call'
  },

  // Identity Protection
  {
    id: 'ip-risk-detections',
    category: 'advanced',
    name: 'Risk Detections',
    method: 'GET',
    endpoint: '/identityProtection/riskDetections',
    body: '',
    headers: '{}',
    description: 'Lists risk detections from Identity Protection'
  },
  {
    id: 'ip-risky-users',
    category: 'advanced',
    name: 'Risky Users',
    method: 'GET',
    endpoint: '/identityProtection/riskyUsers',
    body: '',
    headers: '{}',
    description: 'Lists users at risk detected by Identity Protection'
  },
  {
    id: 'ip-confirm-compromise',
    category: 'advanced',
    name: 'Confirm User Compromised',
    method: 'POST',
    endpoint: '/identityProtection/riskyUsers/confirmCompromised',
    body: JSON.stringify({
      "userIds": [
        "00000000-0000-0000-0000-000000000000" // Replace with actual user ID
      ]
    }, null, 2),
    headers: '{}',
    description: 'Confirms one or more users as compromised'
  },

  // Authentication Methods
  {
    id: 'auth-methods-policy',
    category: 'policies',
    name: 'Authentication Methods Policy',
    method: 'GET',
    endpoint: '/policies/authenticationMethodsPolicy',
    body: '',
    headers: '{}',
    description: 'Gets the authentication methods policy'
  },
  {
    id: 'auth-methods-user',
    category: 'users',
    name: 'User Auth Methods',
    method: 'GET',
    endpoint: '/users/{user-id}/authentication/methods',
    body: '',
    headers: '{}',
    description: 'Lists authentication methods registered to a user'
  },
  {
    id: 'auth-methods-mfa',
    category: 'users',
    name: 'User MFA Status',
    method: 'GET',
    endpoint: '/reports/authenticationMethods/userRegistrationDetails',
    body: '',
    headers: '{}',
    description: 'Gets MFA registration details for all users'
  }
];
